import { Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { DeviceStatus } from '~/generated/graphql';
import type { DeviceList__FilterCount } from '../../queries/list.generated';
import { useDeviceFilters } from '../context';
import { StatusToggleContainer } from '../lib';

export const StatusToggle = ({
  filterCounts,
}: {
  filterCounts: DeviceList__FilterCount | undefined;
}) => {
  const { t } = useTranslation();
  const { setFilter, filters } = useDeviceFilters();

  const healthyCount = filterCounts?.status.find((x) => x.filter === 'healthy')?.count || 0;
  const errorCount = filterCounts?.status.find((x) => x.filter === 'error')?.count || 0;

  const isActive = (desired: DeviceStatus[]) => {
    const selected = filters.status.items.filter((f) => f.selected).map((f) => f.value);

    if (selected.length !== desired.length) return false;
    return desired.every((status) => selected.includes(status));
  };
  return (
    <StatusToggleContainer aria-label={t('pages.devices.list.status_toggle.label')} role="group">
      <Button
        size="small"
        variant="contained"
        onClick={() =>
          setFilter({
            field: 'status',
            items: [
              {
                label: t('pages.devices.list.status_toggle.online'),
                selected: false,
                value: 'HEALTHY',
              },
              {
                label: t('pages.devices.list.status_toggle.offline'),
                selected: false,
                value: 'ERROR',
              },
              {
                label: t('pages.devices.list.status_toggle.warning'),
                selected: false,
                value: 'WARN',
              },
            ],
          })
        }
        aria-pressed={isActive([])}
        sx={{
          backgroundColor: isActive([]) ? 'common.white' : 'transparent',
          color: isActive([]) ? 'primary.text' : 'text.secondary',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {t('pages.devices.list.status_toggle.all')}
      </Button>

      <Button
        size="small"
        variant="contained"
        onClick={() =>
          setFilter({
            field: 'status',
            items: [
              {
                label: t('pages.devices.list.status_toggle.online'),
                selected: true,
                value: 'HEALTHY',
              },
              {
                label: t('pages.devices.list.status_toggle.offline'),
                selected: false,
                value: 'ERROR',
              },
              {
                label: t('pages.devices.list.status_toggle.warning'),
                selected: false,
                value: 'WARN',
              },
            ],
            open: true,
          })
        }
        aria-pressed={isActive(['HEALTHY'])}
        sx={{
          backgroundColor: isActive(['HEALTHY']) ? 'common.white' : 'transparent',
          color: isActive(['HEALTHY']) ? 'primary.text' : 'text.secondary',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {t('pages.devices.list.status_toggle.online')} (
        {filterCounts ? (
          healthyCount
        ) : (
          <CircularProgress aria-label={t('common.status.loading')} size={12} color="inherit" />
        )}
        )
      </Button>

      <Button
        size="small"
        variant="contained"
        onClick={() =>
          setFilter({
            field: 'status',
            items: [
              {
                label: t('pages.devices.list.status_toggle.online'),
                selected: false,
                value: 'HEALTHY',
              },
              {
                label: t('pages.devices.list.status_toggle.offline'),
                selected: true,
                value: 'ERROR',
              },
              {
                label: t('pages.devices.list.status_toggle.warning'),
                selected: false,
                value: 'WARN',
              },
            ],
            open: true,
          })
        }
        aria-pressed={isActive(['ERROR'])}
        sx={{
          backgroundColor: isActive(['ERROR']) ? 'common.white' : 'transparent',
          color: isActive(['ERROR']) ? 'primary.text' : 'text.secondary',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {t('pages.devices.list.status_toggle.offline')} (
        {filterCounts ? (
          errorCount
        ) : (
          <CircularProgress aria-label={t('common.status.loading')} size={12} color="inherit" />
        )}
        )
      </Button>
    </StatusToggleContainer>
  );
};
